import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, Location } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ToastProvider } from './contexts/ToastContext';
import { SubscriptionProvider } from './contexts/SubscriptionContext';
import { LoadingProvider } from './components/LoadingProvider';
import { RequireSubscription } from './middleware/requireSubscription';

// Layouts
import AuthLayout from './layouts/AuthLayout';
import DashboardLayout from './layouts/DashboardLayout';
import AdminLayout from './layouts/AdminLayout';

// Public pages
import HomePage from './pages/public/HomePage';
import PricingPage from './pages/public/PricingPage';
import AboutPage from './pages/public/AboutPage';
import PrivacyPage from './pages/public/PrivacyPage';
import TermsPage from './pages/public/TermsPage';
import FeedbackFormPage from './pages/public/FeedbackFormPage';
import ThankYouPage from './pages/public/ThankYouPage';

// Auth pages
import LoginPage from './pages/auth/LoginPage';
import SignupPage from './pages/auth/SignupPage';
import ForgotPasswordPage from './pages/auth/ForgotPasswordPage';
import ResetPasswordPage from './pages/auth/ResetPasswordPage';

// Dashboard pages
import DashboardHomePage from './pages/dashboard/DashboardHomePage';
import FormsPage from './pages/dashboard/FormsPage';
import FormBuilderPage from './pages/dashboard/FormBuilderPage';
import ResponsesPage from './pages/dashboard/ResponsesPage';
import OrderStickersPage from './pages/dashboard/OrderStickersPage';
import AccountPage from './pages/dashboard/AccountPage';
import BillingPage from './pages/dashboard/BillingPage';

// Admin pages
import AdminDashboardPage from './pages/admin/AdminDashboardPage';
import AdminUsersPage from './pages/admin/AdminUsersPage';
import AdminFormsPage from './pages/admin/AdminFormsPage';
import AdminAnalyticsPage from './pages/admin/AdminAnalyticsPage';

// Protected route component
const ProtectedRoute = ({ children, requiredRole = 'user' }: { children: React.ReactNode; requiredRole?: 'user' | 'admin' }) => {
  const { user, profile, loading } = useAuth();
  const location = useLocation();

  // Show loading while auth is loading OR while profile is loading for admin routes
  if (loading || (requiredRole === 'admin' && user && !profile)) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (requiredRole === 'admin' && profile?.role !== 'admin') {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

// Public route component
const PublicRoute = ({ children }: { children: React.ReactNode }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (user) {
    const destination = location.state?.from?.pathname || '/dashboard';
    return <Navigate to={destination} replace />;
  }

  return children;
};

// API route handler component
const ApiRoute = () => {
  const location = useLocation();
  // For API routes, just return null to prevent any client-side routing
  return null;
};

// Not Found component
const NotFound = () => {
  const location = useLocation();
  // If it's an API route, don't redirect
  if (location.pathname.startsWith('/api/')) {
    return null;
  }
  // Otherwise, redirect to home
  return <Navigate to="/" replace />;
};

function App() {
  return (
    <Router>
      <ToastProvider>
        <AuthProvider>
          <SubscriptionProvider>
            <LoadingProvider>
              <Routes>
                {/* API routes - prevent React Router from handling these */}
                <Route path="/api/*" element={<ApiRoute />} />

                {/* Public routes */}
                <Route path="/" element={<HomePage />} />
                <Route path="/pricing" element={<PricingPage />} />
                <Route path="/about" element={<AboutPage />} />
                <Route path="/privacy" element={<PrivacyPage />} />
                <Route path="/terms" element={<TermsPage />} />
                <Route path="/feedback/:formId" element={<FeedbackFormPage />} />
                <Route path="/thank-you" element={<ThankYouPage />} />

                {/* Auth routes */}
                <Route element={<AuthLayout />}>
                  <Route
                    path="/login"
                    element={
                      <PublicRoute>
                        <LoginPage />
                      </PublicRoute>
                    }
                  />
                  <Route
                    path="/signup"
                    element={
                      <PublicRoute>
                        <SignupPage />
                      </PublicRoute>
                    }
                  />
                  <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                  <Route path="/reset-password" element={<ResetPasswordPage />} />
                </Route>

                {/* Dashboard routes */}
                <Route
                  element={
                    <ProtectedRoute>
                      <RequireSubscription>
                        <DashboardLayout />
                      </RequireSubscription>
                    </ProtectedRoute>
                  }
                >
                  <Route path="/dashboard" element={<DashboardHomePage />} />
                  <Route path="/dashboard/forms" element={<FormsPage />} />
                  <Route path="/dashboard/forms/new" element={<FormBuilderPage />} />
                  <Route path="/dashboard/forms/:formId" element={<FormBuilderPage />} />
                  <Route path="/dashboard/responses" element={<ResponsesPage />} />
                  <Route path="/dashboard/responses/:formId" element={<ResponsesPage />} />
                  <Route path="/dashboard/order-stickers" element={<OrderStickersPage />} />
                  <Route path="/dashboard/account" element={<AccountPage />} />
                  <Route path="/dashboard/billing" element={<BillingPage />} />
                </Route>

                {/* Admin routes */}
                <Route
                  element={
                    <ProtectedRoute requiredRole="admin">
                      <AdminLayout />
                    </ProtectedRoute>
                  }
                >
                  <Route path="/admin" element={<AdminDashboardPage />} />
                  <Route path="/admin/users" element={<AdminUsersPage />} />
                  <Route path="/admin/forms" element={<AdminFormsPage />} />
                  <Route path="/admin/analytics" element={<AdminAnalyticsPage />} />
                </Route>

                {/* Fallback route */}
                <Route path="*" element={<NotFound />} />
              </Routes>
            </LoadingProvider>
          </SubscriptionProvider>
        </AuthProvider>
      </ToastProvider>
    </Router>
  );
}

export default App;